"use client";
import React, { useState, useEffect } from "react";
import { FiChevronDown, FiChevronUp, FiTrash2 } from "react-icons/fi";
import axios from "axios";
import toast from "react-hot-toast";
import Constant from "../helper/Constant";

import "../styles/checkout.css";
import "../styles/address.css";
import "../styles/cart.css";

const getGuestCartId = () => {
  let guestCartId = localStorage.getItem("guestCartId");

  if (!guestCartId) {
    guestCartId = `guest_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("guestCartId", guestCartId);
  }

  return guestCartId;
};

const getCartHeaders = () => {
  const token = localStorage.getItem("token");

  return token ? { Authorization: token } : {};
};

const getGuestCartParams = () => {
  const token = localStorage.getItem("token");

  return token ? {} : { guest_id: getGuestCartId() };
};

export default function Checkout() {
  const CUSTOMISATION_CHARGE = 150;
  const [activeStep, setActiveStep] = useState(1);
  const [paymentMode, setPaymentMode] = useState("online");

  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [loadingAddress, setLoadingAddress] = useState(false);

  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  const selectedAddress =
    addresses.find((addr) => addr._id === selectedAddressId) || null;

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  /* ---------------- TOKEN CHECK ---------------- */

  useEffect(() => {
    // if (!token) {
    //   localStorage.setItem("redirectAfterLogin", "/checkout");

    //   window.location.href = "/login";

    //   return;
    // }

    getCart();
    fetchAddresses();
  }, []);

  useEffect(() => {
    if (!cart.length) return;

    const allCodEnabled = cart.every(
      (item) => String(item.product_id?.cod_enabled) === "true",
    );

    if (!allCodEnabled && paymentMode === "cod") {
      setPaymentMode("online");
    }
  }, [cart, paymentMode]);

  /* ---------------- GET CART ---------------- */

  const getCart = async () => {
    try {
      setLoading(true);

      const res = await axios.get(Constant.postUrls.getMyCartItems, {
        headers: getCartHeaders(),
        params: getGuestCartParams(),
      });

      setCart(res.data.data.docs || []);
    } catch {
      toast.error("Failed to load cart");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- GET ADDRESSES ---------------- */

  const fetchAddresses = async () => {
    try {
      setLoadingAddress(true);

      const res = await axios.post(
        Constant.postUrls.getMyAddresses,
        {},
        {
          headers: getCartHeaders(),
          params: getGuestCartParams(),
        },
      );

      const addr = res.data.data.docs || [];

      setAddresses(addr);

      if (addr.length > 0) {
        setSelectedAddressId(addr[0]._id);
      }
    } catch {
      toast.error("Failed to load addresses");
    } finally {
      setLoadingAddress(false);
    }
  };

  /* ---------------- UPDATE QTY ---------------- */

  const increaseQty = async (id, currentQty) => {
    try {
      await axios.put(
        Constant.postUrls.updateCartQty + "/" + id,
        { quantity: currentQty + 1 },
        {
          headers: getCartHeaders(),
          params: getGuestCartParams(),
        },
      );

      getCart();
    } catch {
      toast.error("Failed to update quantity");
    }
  };

  const decreaseQty = async (id, currentQty) => {
    if (currentQty <= 1) return;

    try {
      await axios.put(
        Constant.postUrls.updateCartQty + "/" + id,
        { quantity: currentQty - 1 },
        {
          headers: getCartHeaders(),
          params: getGuestCartParams(),
        },
      );

      getCart();
    } catch {
      toast.error("Failed to update quantity");
    }
  };

  /* ---------------- REMOVE ITEM ---------------- */

  const removeItem = async (id) => {
  try {
    await axios.delete(Constant.postUrls.removeCartItem + "/" + id, {
      headers: getCartHeaders(),
      params: getGuestCartParams(),
    });

    toast.success("Item removed");

    getCart(); // refresh cart page

    window.dispatchEvent(new Event("cartUpdated"));

  } catch {
    toast.error("Failed to remove item");
  }
};

  /* ---------------- CALCULATIONS ---------------- */

  const subtotal = cart.reduce((sum, item) => {
    const price =
      item.variant_id?.offer_price ||
      item.variant_id?.price ||
      item.product_id?.offer_price ||
      item.product_id?.price ||
      0;

    return sum + price * item.quantity;
  }, 0);

  const deliveryCharges = cart.reduce((sum, item) => {
    const charge = Number(item.product_id?.delivery_charge ?? 0);

    return sum + charge * item.quantity;
  }, 0);

  const customisationTax = cart.reduce((sum, item) => {
    const hasCustomName = String(item.custom_text || "").trim().length > 0;

    return sum + (hasCustomName ? CUSTOMISATION_CHARGE * item.quantity : 0);
  }, 0);

  const discount = 0;

  const total = subtotal + deliveryCharges + customisationTax - discount;
  const codAvailable =
    cart.length > 0 &&
    cart.every((item) => String(item.product_id?.cod_enabled) === "true");

  /* ---------------- ADDRESS FORM ---------------- */

  const handleChange = (field, value) => {
    if (field === "mobile") {
      const cleaned = value.replace(/\D/g, "");

      if (cleaned.length <= 10) {
        setForm({ ...form, mobile: cleaned });
      }

      return;
    }

    setForm({ ...form, [field]: value });
  };

  const saveAddress = async () => {
    try {
      if (form.mobile.length !== 10) {
        toast.error("Mobile must be 10 digits");

        return;
      }

      await axios.post(Constant.postUrls.addAddress, {
        ...form,
        ...getGuestCartParams(),
      }, {
        headers: getCartHeaders(),
      });

      toast.success("Address added");

      setShowForm(false);

      setForm({
        name: "",
        mobile: "",
        line1: "",
        line2: "",
        city: "",
        state: "",
        country: "",
        postalCode: "",
      });

      fetchAddresses();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add address");
    }
  };

  const deleteAddress = async (id) => {
    try {
      await axios.post(
        Constant.postUrls.deleteAddress + id,
        {},
        {
          headers: getCartHeaders(),
          params: getGuestCartParams(),
        },
      );

      toast.success("Address deleted");

      fetchAddresses();
    } catch {
      toast.error("Failed to delete address");
    }
  };

  /* ---------------- PLACE ORDER ---------------- */

  const buildOrderPayload = () => {
    let cartIds = [];
    let productIds = [];
    let productData = [];

    cart.forEach((ele) => {
      cartIds.push(ele._id);
      productIds.push(ele.product_id._id);
      productData.push({
        cart_id: ele._id,
        quantity: ele.quantity,
      });
    });

    return {
      cart_id: cartIds,
      product: productIds,
      product_data: productData,
      address_id: selectedAddressId,
      order_amount: total,
      delivery_charge: deliveryCharges,
      payment_mode: paymentMode,
      ...getGuestCartParams(),
    };
  };

  const placeOrder = async (e) => {
    e.preventDefault();

    try {
      if (!selectedAddressId) {
        toast.error("Please select address");
        return;
      }

      if (cart.length === 0) {
        toast.error("Cart is empty");
        return;
      }

      if (paymentMode === "cod" && !codAvailable) {
        toast.error("Cash on delivery is not available for this product");
        return;
      }

      const payload = buildOrderPayload();

      if (paymentMode === "cod") {
        const res = await axios.post(Constant.mainDomain + "/order/create", payload, {
          headers: getCartHeaders(),
        });

        if (res.data.status === "success") {
          toast.success("Order placed successfully");
          setCart([]);
          window.dispatchEvent(new Event("cartUpdated"));

          setTimeout(() => {
            window.location.href = "/";
          }, 1500);
        }

        return;
      }

      /* STEP 1: CREATE RAZORPAY ORDER */
      const res = await axios.post(
        Constant.mainDomain + "/order/create",
        payload,
        {
          headers: getCartHeaders(),
        }
      );

      const { razorpayOrder, tempOrderData } = res.data;
      localStorage.setItem("razorpay_order_id", razorpayOrder.id);

      /* STEP 2: OPEN RAZORPAY */
      const options = {
        key: "rzp_live_Sg3X7HxmCGJHDu", // 
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "The 3D Creator",
        description: "Order Payment",
        order_id: razorpayOrder.id,

        handler: async function (response) {
          try {
            /* STEP 3: VERIFY PAYMENT */
            const verifyRes = await axios.post(
              Constant.mainDomain + "/order/payment/verify",
              {
                razorpay_order_id: response.razorpay_order_id,  
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                orderData: tempOrderData,
                ...getGuestCartParams(),
              },
              {
                headers: getCartHeaders(),
              }
            );

            if (verifyRes.data.status === "success") {
              toast.success("Payment successful");

              setCart([]);
              window.dispatchEvent(new Event("cartUpdated"));

              setTimeout(() => {
                window.location.href = "/";
              }, 1500);
            } else {
              toast.error("Payment verification failed");
            }
          } catch (err) {
            toast.error("Payment verification error");
          }
        },

        prefill: {
          name: selectedAddress?.name || storedUser?.name || "Customer",
          email: storedUser?.email || "",
          contact:
            selectedAddress?.mobile ||
            storedUser?.mobile_no ||
            storedUser?.mobile ||
            "",
        },

        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (response) {
        console.log("Payment Failed:", response);

        toast.error("Payment failed. Please try again");

        localStorage.setItem("failed_payment", JSON.stringify(response.error));
      });
      rzp.open();

    } catch (err) {
      toast.error(err?.response?.data?.message || "Order failed");
    }
  };


  // const placeOrder = async (e) => {
  //   e.preventDefault();

  //   try {
  //     if (!selectedAddressId) {
  //       toast.error("Please select address");

  //       return;
  //     }

  //     if (cart.length === 0) {
  //       toast.error("Cart is empty");

  //       return;
  //     }

  //     let cartIds = [];
  //     let productIds = [];
  //     let productData = [];

  //     cart.forEach((ele) => {
  //       cartIds.push(ele._id);

  //       productIds.push(ele.product_id._id);

  //       productData.push({
  //         cart_id: ele._id,
  //         quantity: ele.quantity,
  //       });
  //     });

  //     const payload = {
  //       cart_id: cartIds,
  //       product: productIds,
  //       product_data: productData,
  //       address_id: selectedAddressId,
  //       order_amount: total,
  //     };

  //     const res = await axios.post(Constant.postUrls.placeOrder, payload, {
  //       headers: {
  //         Authorization: localStorage.getItem("token"),
  //       },
  //     });

  //     if (res.data.status === "success") {
  //       toast.success(res.data.message);

  //       setCart([]);

  //       setTimeout(() => {
  //         window.location.href = "/";
  //       }, 1500);
  //     } else {
  //       toast.error(res.data.message);
  //     }
  //   } catch (err) {
  //     toast.error(err?.response?.data?.message || "Order failed");
  //   }
  // };

  /* ---------------- STEP HEADER ---------------- */

  const StepHeader = ({ stepNumber, title }) => {
    const isActive = activeStep === stepNumber;
    const isCompleted = activeStep > stepNumber;

    return (
      <div
        className={`checkout-band ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
        onClick={() => setActiveStep(stepNumber)}
      >
        <div className="checkout-band-left">
          <div className="checkout-band-badge">
            {isCompleted ? "✓" : stepNumber}
          </div>

          <span>{title}</span>
        </div>

        <div>{isActive ? <FiChevronUp /> : <FiChevronDown />}</div>
      </div>
    );
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="checkout-container">
      {/* STEP 1 CART */}

      <div className="checkout-step">
        <StepHeader stepNumber={1} title="Cart Summary" />

        {activeStep === 1 && (
          <div className="checkout-step-content">
            {cart.map((item) => {
              const price =
                item.variant_id?.offer_price ||
                item.variant_id?.price ||
                item.product_id?.offer_price ||
                item.product_id?.price ||
                0;

              const image =
                item.variant_id?.images?.[0] || item.product_id?.images?.[0];

              return (
                <div key={item._id} className="cart-item">
                  <img
                    src={Constant.imageDomain + image}
                    className="cart-image"
                  />

                  <div className="cart-info">
                    <div className="cart-name">{item.product_id?.name}</div>

                    <div className="cart-variant">
                      {item.variant_id?.size} {item.variant_id?.colour}
                    </div>

                    {item.custom_text ? (
                      <div className="cart-variant">
                        Custom Name: {item.custom_text}
                      </div>
                    ) : null}

                    <div className="cart-price">₹{price.toLocaleString()}</div>
                    {item.custom_text ? (
                      <div className="cart-variant">
                        Customization: ₹
                        {(CUSTOMISATION_CHARGE * item.quantity).toLocaleString()}
                      </div>
                    ) : null}
                  </div>

                  <div className="cart-qty">
                    <button
                      className="qty-btn"
                      onClick={() => decreaseQty(item._id, item.quantity)}
                    >
                      −
                    </button>

                    <div className="qty-number">{item.quantity}</div>

                    <button
                      className="qty-btn"
                      onClick={() => increaseQty(item._id, item.quantity)}
                    >
                      +
                    </button>
                  </div>

                  <button
                    className="cart-delete"
                    onClick={() => removeItem(item._id)}
                  >
                    🗑
                  </button>
                </div>
              );
            })}

            <div className="cart-summary">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </div>

              <div className="summary-row">
                <span>Discount</span>
                <span>−₹{discount.toLocaleString()}</span>
              </div>

              <div className="summary-row">
                <span>Delivery Charges</span>
                <span>₹{deliveryCharges.toLocaleString()}</span>
              </div>

              {customisationTax > 0 ? (
                <div className="summary-row">
                  <span>Customization Tax</span>
                  <span>₹{customisationTax.toLocaleString()}</span>
                </div>
              ) : null}

              <div className="summary-total">
                <span>Total</span>
                <span>₹{total.toLocaleString()}</span>
              </div>

              <button className="primary-btn" onClick={() => setActiveStep(2)}>
                Proceed to Address
              </button>
            </div>
          </div>
        )}
      </div>

      {/* STEP 2 ADDRESS */}

      <div className="checkout-step" style={{ marginTop: "20px" }}>
        <StepHeader stepNumber={2} title="Delivery Address" />

        {activeStep === 2 && (
          <div className="checkout-step-content">
            {addresses.map((addr) => (
              <div
                key={addr._id}
                className={`address-card ${selectedAddressId === addr._id ? "selected" : ""}`}
              >
                <div>
                  <div>
                    {addr.name} • {addr.mobile}
                  </div>

                  <div>{addr.line1}</div>

                  <div>{addr.line2}</div>

                  <div>
                    {addr.city}, {addr.state}
                  </div>

                  <div>
                    {addr.country} − {addr.postalCode}
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    className="secondary-btn"
                    onClick={() => setSelectedAddressId(addr._id)}
                  >
                    Deliver Here
                  </button>

                  <button
                    onClick={() => deleteAddress(addr._id)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: "#dc2626",
                      fontSize: "18px",
                    }}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}

            {!showForm && (
              <button className="outline-btn" onClick={() => setShowForm(true)}>
                + Add New Address
              </button>
            )}

            {showForm && (
              <div className="address-form">
                <input
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />

                <input
                  placeholder="WhatsApp Number"
                  value={form.mobile}
                  onChange={(e) => handleChange("mobile", e.target.value)}
                />

                <input
                  placeholder="Address Line 1"
                  value={form.line1}
                  onChange={(e) => handleChange("line1", e.target.value)}
                />

                <input
                  placeholder="Address Line 2"
                  value={form.line2}
                  onChange={(e) => handleChange("line2", e.target.value)}
                />

                <input
                  placeholder="City"
                  value={form.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                />

                <input
                  placeholder="State"
                  value={form.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                />

                <input
                  placeholder="Country"
                  value={form.country}
                  onChange={(e) => handleChange("country", e.target.value)}
                />

                <input
                  placeholder="Postal Code"
                  value={form.postalCode}
                  onChange={(e) => handleChange("postalCode", e.target.value)}
                />

                <button className="primary-btn" onClick={saveAddress}>
                  Save Address
                </button>
              </div>
            )}

            <button
              className="primary-btn"
              onClick={() => {
                if (!selectedAddressId) {
                  toast.error("Please select address");
                  return;
                }

                setActiveStep(3);
              }}
            >
              Proceed to Payment
            </button>
          </div>
        )}
      </div>

      <div className="checkout-step" style={{ marginTop: "20px" }}>
        <StepHeader stepNumber={3} title="Payment Method" />

        {activeStep === 3 && (
          <div className="checkout-step-content">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "14px",
                marginBottom: "24px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  opacity: codAvailable ? 1 : 0.6,
                }}
              >
                <input
                  type="radio"
                  name="paymentMode"
                  value="cod"
                  checked={paymentMode === "cod"}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  disabled={!codAvailable}
                />
                Cash On Delivery
              </label>

              {!codAvailable ? (
                <div style={{ color: "#b91c1c", fontSize: "14px" }}>
                  cash on delivery is not available for this product
                </div>
              ) : null}

              <label
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <input
                  type="radio"
                  name="paymentMode"
                  value="online"
                  checked={paymentMode === "online"}
                  onChange={(e) => setPaymentMode(e.target.value)}
                />
                Online Payment
              </label>
            </div>

            <div className="cart-summary">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString()}</span>
              </div>

              <div className="summary-row">
                <span>Delivery Charges</span>
                <span>₹{deliveryCharges.toLocaleString()}</span>
              </div>

              <div className="summary-row">
                <span>Discount</span>
                <span>−₹{discount.toLocaleString()}</span>
              </div>

              <div className="summary-total">
                <span>Total Amount to Pay</span>
                <span>₹{total.toLocaleString()}</span>
              </div>

              <button className="primary-btn" onClick={placeOrder}>
                {paymentMode === "cod" ? "Place Order" : "Pay Now"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// "use client";
// import React, { useState } from "react";
// import { FiChevronDown, FiChevronUp } from "react-icons/fi";
// import "../styles/checkout.css";
// import "../styles/address.css";
// import "../styles/cart.css";

// export default function Checkout() {

//   /* ---------------- STEP STATE ---------------- */

//   const [activeStep, setActiveStep] = useState(1);

//   /* ---------------- CART STATE ---------------- */

//   const [cart, setCart] = useState([
//     {
//       id: 1,
//       name: "Galaxy S22 Ultra",
//       variant: "Phantom Black",
//       price: 125999,
//       qty: 1,
//       image:
//         "https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?w=200",
//     },
//     {
//       id: 2,
//       name: "Galaxy M34 5G",
//       variant: "Prism Silver",
//       price: 14499,
//       qty: 2,
//       image:
//         "https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=200",
//     },
//   ]);

//   const increaseQty = (id) => {
//     setCart(cart.map(item =>
//       item.id === id
//         ? { ...item, qty: item.qty + 1 }
//         : item
//     ));
//   };

//   const decreaseQty = (id) => {
//     setCart(cart.map(item =>
//       item.id === id && item.qty > 1
//         ? { ...item, qty: item.qty - 1 }
//         : item
//     ));
//   };

//   const removeItem = (id) => {
//     setCart(cart.filter(item => item.id !== id));
//   };

//   const subtotal = cart.reduce(
//     (sum, item) => sum + item.price * item.qty,
//     0
//   );

//   const discount = 9200;
//   const total = subtotal - discount;

//   /* ---------------- ADDRESS STATE ---------------- */

//   const [addresses, setAddresses] = useState([
//     {
//       id: 1,
//       name: "Shubham Muneshwar",
//       mobile: "9876543210",
//       line1: "Flat No 302, Sai Residency",
//       line2: "MG Road",
//       city: "Bangalore",
//       state: "Karnataka",
//       country: "India",
//       postalCode: "560001",
//     },
//   ]);

//   const [selectedAddressId, setSelectedAddressId] =
//     useState(1);

//   const [showForm, setShowForm] = useState(false);

//   const [form, setForm] = useState({
//     name: "",
//     mobile: "",
//     line1: "",
//     line2: "",
//     city: "",
//     state: "",
//     country: "",
//     postalCode: "",
//   });

//   const handleChange = (field, value) => {

//     if (field === "mobile") {
//       const cleaned = value.replace(/\D/g, "");

//       if (cleaned.length <= 10)
//         setForm({ ...form, mobile: cleaned });

//       return;
//     }

//     setForm({ ...form, [field]: value });
//   };

//   const saveAddress = () => {

//     if (form.mobile.length !== 10) {
//       alert("Mobile must be 10 digits");
//       return;
//     }

//     const newAddress = {
//       id: Date.now(),
//       ...form,
//     };

//     setAddresses([...addresses, newAddress]);

//     setSelectedAddressId(newAddress.id);

//     setShowForm(false);

//     setForm({
//       name: "",
//       mobile: "",
//       line1: "",
//       line2: "",
//       city: "",
//       state: "",
//       country: "",
//       postalCode: "",
//     });
//   };

//   /* ---------------- STEP HEADER ---------------- */

//   const StepHeader = ({ stepNumber, title }) => {

//     const isActive = activeStep === stepNumber;
//     const isCompleted = activeStep > stepNumber;

//     return (
//       <div
//         className={`checkout-band
//           ${isActive ? "active" : ""}
//           ${isCompleted ? "completed" : ""}
//         `}
//         onClick={() => setActiveStep(stepNumber)}
//       >

//         <div className="checkout-band-left">

//           <div className="checkout-band-badge">
//             {isCompleted ? "✓" : stepNumber}
//           </div>

//           <span>{title}</span>

//         </div>

//         <div>
//           {isActive ? (
//             <FiChevronUp />
//           ) : (
//             <FiChevronDown />
//           )}
//         </div>

//       </div>
//     );
//   };

//   /* ---------------- UI ---------------- */

//   return (
//     <div className="checkout-container">

//       {/* STEP 1 CART */}

//       <div className="checkout-step">

//         <StepHeader
//           stepNumber={1}
//           title="Cart Summary"
//         />

//         {activeStep === 1 && (

//           <div className="checkout-step-content">

//             {cart.map(item => (

//               <div key={item.id} className="cart-item">

//                 <img
//                   src={item.image}
//                   className="cart-image"
//                 />

//                 <div className="cart-info">

//                   <div className="cart-name">
//                     {item.name}
//                   </div>

//                   <div className="cart-variant">
//                     {item.variant}
//                   </div>

//                   <div className="cart-price">
//                     ₹{item.price.toLocaleString()}
//                   </div>

//                 </div>

//                 <div className="cart-qty">

//   <button
//     className="qty-btn"
//     onClick={() => decreaseQty(item.id)}
//   >
//     −
//   </button>

//   <div className="qty-number">
//     {item.qty}
//   </div>

//   <button
//     className="qty-btn"
//     onClick={() => increaseQty(item.id)}
//   >
//     +
//   </button>

// </div>

// <button
//   className="cart-delete"
//   onClick={() => removeItem(item.id)}
// >
//   🗑
// </button>

//               </div>

//             ))}

//             <div className="cart-summary">

//               <div className="summary-row">
//                 <span>Subtotal</span>
//                 <span>
//                   ₹{subtotal.toLocaleString()}
//                 </span>
//               </div>

//               <div className="summary-row">
//                 <span>Discount</span>
//                 <span>
//                   −₹{discount.toLocaleString()}
//                 </span>
//               </div>

//               <div className="summary-total">
//                 <span>Total</span>
//                 <span>
//                   ₹{total.toLocaleString()}
//                 </span>
//               </div>

//               <button
//                 className="primary-btn"
//                 onClick={() =>
//                   setActiveStep(2)
//                 }
//               >
//                 Proceed to Address
//               </button>

//             </div>

//           </div>

//         )}

//       </div>

//       {/* STEP 2 ADDRESS */}

//       <div className="checkout-step" style={{ marginTop: "20px" }}>

//         <StepHeader
//           stepNumber={2}
//           title="Delivery Address"
//         />

//         {activeStep === 2 && (

//           <div className="checkout-step-content">

//             {addresses.map(addr => (

//               <div
//                 key={addr.id}
//                 className={`address-card
//                   ${
//                     selectedAddressId === addr.id
//                       ? "selected"
//                       : ""
//                   }
//                 `}
//               >

//                 <div>

//                   <div>
//                     {addr.name} • {addr.mobile}
//                   </div>

//                   <div>{addr.line1}</div>

//                   <div>{addr.line2}</div>

//                   <div>
//                     {addr.city}, {addr.state}
//                   </div>

//                   <div>
//                     {addr.country} −{" "}
//                     {addr.postalCode}
//                   </div>

//                 </div>

//                 <button
//                   className="secondary-btn"
//                   onClick={() =>
//                     setSelectedAddressId(
//                       addr.id
//                     )
//                   }
//                 >
//                   Deliver Here
//                 </button>

//               </div>

//             ))}

//             {!showForm && (

//               <button
//                 className="outline-btn"
//                 onClick={() =>
//                   setShowForm(true)
//                 }
//               >
//                 + Add New Address
//               </button>

//             )}

//             {showForm && (

//               <div className="address-form">

//                 <input
//                   placeholder="Name"
//                   value={form.name}
//                   onChange={(e) =>
//                     handleChange(
//                       "name",
//                       e.target.value
//                     )
//                   }
//                 />

//                 <input
//                   placeholder="Mobile"
//                   value={form.mobile}
//                   onChange={(e) =>
//                     handleChange(
//                       "mobile",
//                       e.target.value
//                     )
//                   }
//                 />

//                 <input
//                   placeholder="Address Line 1"
//                   onChange={(e) =>
//                     handleChange(
//                       "line1",
//                       e.target.value
//                     )
//                   }
//                 />

//                 <input
//                   placeholder="City"
//                   onChange={(e) =>
//                     handleChange(
//                       "city",
//                       e.target.value
//                     )
//                   }
//                 />

//                 <input
//                   placeholder="State"
//                   onChange={(e) =>
//                     handleChange(
//                       "state",
//                       e.target.value
//                     )
//                   }
//                 />

//                 <input
//                   placeholder="Country"
//                   onChange={(e) =>
//                     handleChange(
//                       "country",
//                       e.target.value
//                     )
//                   }
//                 />

//                 <input
//                   placeholder="Postal Code"
//                   onChange={(e) =>
//                     handleChange(
//                       "postalCode",
//                       e.target.value
//                     )
//                   }
//                 />

//                 <button
//                   className="primary-btn"
//                   onClick={saveAddress}
//                 >
//                   Save Address
//                 </button>

//               </div>

//             )}

//             <button className="primary-btn">
//               Place Order
//             </button>

//           </div>

//         )}

//       </div>

//     </div>
//   );
// }
